/**
 * 캐럿 내비게이션 (방향키) — 블록 간 이동 + 인라인 코드 탈출.
 *
 * 배경: 각 블록의 `.content`가 **독립 contenteditable 섬**이라 네이티브 방향키는
 *   블록 경계를 넘지 못한다(한 블록 안에 캐럿이 갇힘). 이 모듈이 경계에서 인접 블록으로
 *   캐럿을 넘긴다.
 *
 *  - ArrowLeft(블록 시작·collapsed)  → 이전 편집영역 끝
 *  - ArrowRight(블록 끝·collapsed)   → 다음 편집영역 시작
 *  - ArrowUp(첫 줄)                  → 이전 편집영역 마지막 줄(가로 위치 보존)
 *  - ArrowDown(마지막 줄)            → 다음 편집영역 첫 줄(가로 위치 보존)
 *  - 인라인 코드 탈출: 코드 끝에서 ArrowRight / 코드 시작에서 ArrowLeft → 코드 밖으로,
 *    코드 끝에서 스페이스 2번(직전 문자도 스페이스) → 코드 밖 공백 1개 + 캐럿 탈출.
 *
 * 표 셀(.lumir-tbl 내부)은 table-controls가 자체 방향키를 소유하므로 건드리지 않는다.
 * 레이아웃 정보(getClientRects)가 없는 환경(jsdom 등)에서는 단일 줄로 간주해 이동을 허용한다.
 */

function setRange(range) {
  const s = window.getSelection();
  if (!s) return;
  s.removeAllRanges();
  s.addRange(range);
}
function focusStart(content) {
  content.focus();
  const r = document.createRange();
  r.selectNodeContents(content);
  r.collapse(true);
  setRange(r);
}
function focusEnd(content) {
  content.focus();
  const r = document.createRange();
  r.selectNodeContents(content);
  r.collapse(false);
  setRange(r);
}

/** el 내용 기준 캐럿이 시작(끝)인지 — 레이아웃 비의존(오프셋 기반) */
function atStartOf(el, range) {
  const r = range.cloneRange();
  r.selectNodeContents(el);
  r.setEnd(range.startContainer, range.startOffset);
  return r.toString().length === 0;
}
function atEndOf(el, range) {
  const r = range.cloneRange();
  r.selectNodeContents(el);
  r.setStart(range.endContainer, range.endOffset);
  return r.toString().length === 0;
}

const ZWSP = "​"; // 코드 밖 캐럿 앵커(직렬화에서 제거) — 빈 텍스트 노드는 브라우저가 코드로 재흡수함

/** 코드 끝에서 오른쪽 탈출 — 코드 밖 평문 위치로 캐럿 이동. 반환: DOM 변경 여부 */
function escapeCodeRight(code) {
  const after = code.nextSibling;
  if (after && after.nodeType === 3) {
    const r = document.createRange();
    r.setStart(after, 0);
    r.collapse(true);
    setRange(r);
    return false;
  }
  const zwsp = document.createTextNode(ZWSP);
  code.parentNode.insertBefore(zwsp, code.nextSibling);
  const r = document.createRange();
  r.setStart(zwsp, 1);
  r.collapse(true);
  setRange(r);
  return true;
}
/** 코드 시작에서 왼쪽 탈출 — 코드 밖 평문 위치로 캐럿 이동. 반환: DOM 변경 여부 */
function escapeCodeLeft(code) {
  const before = code.previousSibling;
  if (before && before.nodeType === 3) {
    const r = document.createRange();
    r.setStart(before, before.length);
    r.collapse(true);
    setRange(r);
    return false;
  }
  const zwsp = document.createTextNode(ZWSP);
  code.parentNode.insertBefore(zwsp, code);
  const r = document.createRange();
  r.setStart(zwsp, 0);
  r.collapse(true);
  setRange(r);
  return true;
}

/** content 안에서 캐럿을 감싸는 <code> 요소(없으면 null) */
function enclosingCode(node, content) {
  let el = node && node.nodeType === 1 ? node : node && node.parentNode;
  while (el && el !== content) {
    if (el.tagName && el.tagName.toLowerCase() === "code") return el;
    el = el.parentNode;
  }
  return null;
}

/**
 * 편집 가능한 영역 목록(문서 순서) — 텍스트 블록 .content + 표 셀.
 * 표 셀을 포함해 인접 블록에서 방향키로 표에 진입할 수 있게 한다(표 내부 이동은 table-controls 소관 —
 * caret-nav는 .lumir-tbl 안에서는 관여하지 않으므로 충돌 없음).
 */
export function editableContents(editorEl) {
  return [...editorEl.querySelectorAll('.content[contenteditable="true"], td.lumir-cell[contenteditable="true"]')];
}

// ---- 레이아웃(줄) 판정 ----
/**
 * 캐럿 rect — collapsed 캐럿은 브라우저별로 getClientRects/getBoundingClientRect가 0을 주므로,
 * 실패 시 **인접 한 글자 범위**로 확장해 실제 줄 rect를 확보한다(코드블록 첫/마지막 줄 판정 신뢰성).
 */
function caretRect(range) {
  const rects = range.getClientRects && range.getClientRects();
  if (rects && rects.length) {
    const r = rects[rects.length - 1];
    if (r && r.height) return r;
  }
  const b = range.getBoundingClientRect && range.getBoundingClientRect();
  if (b && b.height) return b;
  const node = range.endContainer;
  if (node && node.nodeType === 3) {
    const off = range.endOffset;
    try {
      const p = document.createRange();
      if (off < node.nodeValue.length) { p.setStart(node, off); p.setEnd(node, off + 1); }
      else if (off > 0) { p.setStart(node, off - 1); p.setEnd(node, off); }
      else return null;
      const pr = p.getClientRects();
      if (pr && pr.length && pr[0].height) return pr[0];
      const pb = p.getBoundingClientRect();
      if (pb && pb.height) return pb;
    } catch (_) {}
  }
  return null;
}
/** content 안에서 캐럿 앞(before=true)/뒤에 줄바꿈("\n"·<br>)이 없나 — 레이아웃 없을 때 첫/마지막 줄 폴백 */
function noBreak(content, range, before) {
  const r = range.cloneRange();
  r.selectNodeContents(content);
  if (before) r.setEnd(range.startContainer, range.startOffset);
  else r.setStart(range.endContainer, range.endOffset);
  const frag = r.cloneContents();
  return !/\n/.test(frag.textContent || "") && !frag.querySelector("br");
}
/** content 시작/끝 지점의 캐럿 rect(첫/마지막 시각 줄 기준) — box가 아닌 캐럿 위치라 padding 무관 */
function edgeCaretRect(content, atStart) {
  const r = document.createRange();
  r.selectNodeContents(content);
  r.collapse(atStart);
  return caretRect(r);
}
function atFirstVisualLine(content, range) {
  const cr = caretRect(range);
  const ref = cr && cr.height ? edgeCaretRect(content, true) : null;
  // padding 무관하도록 box가 아닌 첫 줄 캐럿 top과 비교(레이아웃 없으면 캐럿 앞 줄바꿈 유무로 폴백)
  if (cr && cr.height && ref && ref.height) return Math.abs(cr.top - ref.top) < Math.max(4, cr.height * 0.5);
  return noBreak(content, range, true);
}
function atLastVisualLine(content, range) {
  const cr = caretRect(range);
  const ref = cr && cr.height ? edgeCaretRect(content, false) : null;
  if (cr && cr.height && ref && ref.height) return Math.abs(cr.bottom - ref.bottom) < Math.max(4, cr.height * 0.5);
  return noBreak(content, range, false);
}

export function caretRangeFromPoint(x, y, within) {
  let range = null;
  if (typeof document.caretRangeFromPoint === "function") {
    range = document.caretRangeFromPoint(x, y);
  } else if (typeof document.caretPositionFromPoint === "function") {
    const pos = document.caretPositionFromPoint(x, y);
    if (pos) {
      range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
    }
  }
  if (range && within && !within.contains(range.startContainer)) return null;
  return range;
}

/** 세로 이동: 대상 편집영역에 x 좌표를 보존해 캐럿 배치(레이아웃 없으면 시작/끝) */
function placeVertical(target, x, fromTop) {
  target.focus();
  const box = target.getBoundingClientRect && target.getBoundingClientRect();
  if (box && (box.width || box.height) && typeof x === "number") {
    const y = fromTop ? box.top + Math.min(8, box.height / 2) : box.bottom - Math.min(8, box.height / 2);
    const r = caretRangeFromPoint(x, y, target);
    if (r) {
      setRange(r);
      return;
    }
  }
  if (fromTop) focusStart(target);
  else focusEnd(target);
}

export function attachCaretNav(editorEl, opts) {
  opts = opts || {};
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};

  function onKeydown(e) {
    if (e.isComposing || isComposing()) return; // IME 가드
    if (e.metaKey || e.ctrlKey || e.altKey) return; // 조합키는 다른 핸들러 소관
    const key = e.key;
    if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowDown" && key !== " " && key !== "Spacebar") return;

    // 표 셀은 table-controls가 방향키 소유
    if (e.target.closest && e.target.closest(".lumir-tbl")) return;
    const content = e.target.closest && e.target.closest('.content[contenteditable="true"]');
    if (!content || !editorEl.contains(content)) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);

    // --- 인라인 코드 탈출 ---
    const code = range.collapsed ? enclosingCode(range.endContainer, content) : null;
    if (code) {
      // 스페이스로 코드 탈출 — "빠르게 2번"(코드 끝에서):
      //  · 1번째: 코드 안에 직접 삽입(브라우저가 코드 밖으로 빼는 것 방지 → 2번째 감지 보장)
      //  · 2번째(직전이 공백): 코드 밖으로 탈출(코드 내 후행 공백 제거, 밖에 공백 1개, 캐럿 이동)
      if ((key === " " || key === "Spacebar") && atEndOf(code, range)) {
        e.preventDefault();
        const node = range.endContainer;
        const prevSpace =
          node.nodeType === 3 && range.endOffset > 0 && node.nodeValue[range.endOffset - 1] === " ";
        if (prevSpace) {
          node.nodeValue = node.nodeValue.slice(0, range.endOffset - 1) + node.nodeValue.slice(range.endOffset);
          if (node.nodeValue === "") node.remove();
          const spaceNode = document.createTextNode(" ");
          code.parentNode.insertBefore(spaceNode, code.nextSibling);
          const r = document.createRange();
          r.setStart(spaceNode, 1);
          r.collapse(true);
          setRange(r);
        } else {
          const r = range.cloneRange();
          const sp = document.createTextNode(" ");
          r.insertNode(sp);
          const r2 = document.createRange();
          r2.setStart(sp, 1);
          r2.collapse(true);
          setRange(r2);
        }
        onChange();
        return;
      }
      if (key === "ArrowRight" && atEndOf(code, range)) {
        e.preventDefault();
        escapeCodeRight(code); // 코드 밖 평문 위치로(같은 블록에서 일반 텍스트 이어쓰기)
        return;
      }
      if (key === "ArrowLeft" && atStartOf(code, range)) {
        e.preventDefault();
        escapeCodeLeft(code);
        return;
      }
    }

    if (key === " " || key === "Spacebar") return; // 코드 밖 스페이스는 네이티브
    if (!range.collapsed) return; // 선택 상태의 방향키는 네이티브(선택 접기)

    const list = editableContents(editorEl);
    const i = list.indexOf(content);
    if (i < 0) return;
    const prev = list[i - 1] || null;
    const next = list[i + 1] || null;

    if (key === "ArrowLeft") {
      if (atStartOf(content, range) && prev) {
        e.preventDefault();
        focusEnd(prev);
      }
      return;
    }
    if (key === "ArrowRight") {
      if (atEndOf(content, range) && next) {
        e.preventDefault();
        focusStart(next);
      }
      return;
    }
    if (key === "ArrowUp") {
      if (atFirstVisualLine(content, range) && prev) {
        e.preventDefault();
        const cr = caretRect(range);
        placeVertical(prev, cr ? cr.left : undefined, false);
      }
      return;
    }
    if (key === "ArrowDown") {
      if (atLastVisualLine(content, range) && next) {
        e.preventDefault();
        const cr = caretRect(range);
        placeVertical(next, cr ? cr.left : undefined, true);
      }
      return;
    }
  }

  editorEl.addEventListener("keydown", onKeydown);
  return () => editorEl.removeEventListener("keydown", onKeydown);
}
